import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type { Skill } from 'src/app/domain/skills/types/skills.types';
import { SkillCategories } from 'src/app/domain/skills/types/skills.lookups';
import { MeasurementType, MeasurementTypeDisplayName } from 'src/app/domain/observations/types/observations.lookups';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Canonical card for a library {@link Skill}. Displays the skill name, area label,
 * description, and the set of measurement types enabled for this skill.
 *
 * Not FERPA-sensitive — skills are library resources, not student data.
 *
 * @feature shared
 */
@Component({
  selector: 'app-skill-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CardComponent, ChipComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <app-card [button]="clickable()" [ariaLabel]="'Open skill ' + skill().name" (click)="onClick()">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <app-chip [color]="areaColor()" size="xs">{{ skill().area }}</app-chip>
          </div>

          <h3 class="mt-2 truncate text-base font-semibold text-body">{{ skill().name }}</h3>

          @if (skill().description) {
            <p class="mt-1 line-clamp-2 text-sm text-muted">{{ skill().description }}</p>
          }
        </div>

        @if (clickable()) {
          <app-icon name="chevron-forward" size="xs" color="medium" />
        }
      </div>

      @if (enabledTypes().length) {
        <div class="mt-3 flex flex-wrap gap-1 border-t border-outline-low pt-3">
          @for (type of enabledTypes(); track type) {
            <app-chip color="medium" size="xs" [outline]="true">{{ type }}</app-chip>
          }
        </div>
      }
    </app-card>
  `,
})
export class SkillCardComponent {
  /** The skill to display. */
  readonly skill = input.required<Skill>();

  /** Whether the card is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the card is clicked. */
  readonly cardClick = output<Skill>();

  /** Map skill category → chip color. Mirrors the four canonical Blume categories. */
  readonly areaColor = computed<'aqua' | 'coral' | 'sand' | 'purple'>(() => {
    switch (this.skill().category) {
      case SkillCategories.ACADEMIC:
        return 'aqua';
      case SkillCategories.BEHAVIOR_SOCIAL_EMOTIONAL:
        return 'coral';
      case SkillCategories.COMMUNICATION:
        return 'sand';
      default:
        return 'purple';
    }
  });

  /** Display labels for measurement types declared on this skill, in canonical order. */
  readonly enabledTypes = computed<readonly string[]>(() => {
    const s = this.skill();
    const labels: string[] = [];
    for (const type of Object.values(MeasurementType)) {
      if (s.measurements[type]) labels.push(MeasurementTypeDisplayName[type]);
    }
    return labels;
  });

  onClick(): void {
    if (this.clickable()) {
      this.cardClick.emit(this.skill());
    }
  }
}
